import React, { useRef, useState } from 'react';
import { css } from 'emotion';
import { marked } from 'marked';
import Heading from '@amzn/meridian/heading';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Divider from '@amzn/meridian/divider';
import { recordCustomEventInCloudWatchRUM } from '@amzn/carrierlogistics-commons/dist/utils/cloudwatchRum';
import { BUTTON_CLICK } from '@amzn/carrierlogistics-commons/dist/constants/analytics';

import {
  aePrivacyNotice,
  auPrivacyNotice,
  brPrivacyNotice,
  caPrivacyNotice,
  euPrivacyNotice,
  inPrivacyNotice,
  mxPrivacyNotice,
  privacyNoticeContentCountryMap,
  privacyNoticeTitle,
  usPrivacyNotice,
} from '../constants/privacyNotice';
import ConditionOfUsePrivacyNoticePopup from './molecules/ConditionOfUsePrivacyNoticePopup';
import SpanButton from './atoms/SpanButton';
import { COUNTRY_MAP, LOCALES_MAP } from '../constants/common';
import { PRIVACY_BUTTON_REGION } from '../constants/analytics';

const PrivacyNotice = () => {
  const [translationPopupOpen, setTranslationPopupOpen] = useState(false);
  const [popupContent, setPopupContent] = useState('');

  const onTranslationPopupOpen = (locale, country) => {
    setPopupContent(
      marked.parse(privacyNoticeContentCountryMap[country][locale])
    );
    setTranslationPopupOpen(true);
  };
  const onTranslationPopupClose = () => {
    setPopupContent('');
    setTranslationPopupOpen(false);
  };

  const euPrivacyNoticeRef = useRef();
  const usPrivacyNoticeRef = useRef();
  const auPrivacyNoticeRef = useRef();
  const caPrivacyNoticeRef = useRef();
  const aePrivacyNoticeRef = useRef();
  const brPrivacyNoticeRef = useRef();
  const inPrivacyNoticeRef = useRef();
  const mxPrivacyNoticeRef = useRef();

  const countryRefMap = {
    [COUNTRY_MAP.EU]: euPrivacyNoticeRef,
    [COUNTRY_MAP.US]: usPrivacyNoticeRef,
    [COUNTRY_MAP.AU]: auPrivacyNoticeRef,
    [COUNTRY_MAP.CA]: caPrivacyNoticeRef,
    [COUNTRY_MAP.AE]: aePrivacyNoticeRef,
    [COUNTRY_MAP.BR]: brPrivacyNoticeRef,
    [COUNTRY_MAP.IN]: inPrivacyNoticeRef,
    [COUNTRY_MAP.MX]: mxPrivacyNoticeRef,
  };

  const onPrivacyNoticeNavButtonLink = type => {
    if (countryRefMap[type].current?.scrollIntoView) {
      countryRefMap[type].current.scrollIntoView({ behavior: 'smooth' });
    }
    recordCustomEventInCloudWatchRUM(BUTTON_CLICK, {
      action_properties: {
        name: PRIVACY_BUTTON_REGION,
      },
      extra_properties: {
        name: type,
      },
    });
  };

  return (
    <>
      <ConditionOfUsePrivacyNoticePopup
        innerHtmlBodyContent={popupContent}
        open={translationPopupOpen}
        modalTitle="Privacy Notice"
        onClose={onTranslationPopupClose}
      />
      <article
        className={css`
          & {
            font-family: AmazonEmber-Regular, Amazon Ember;
          }
        `}>
        <Heading
          level={1}
          type="d50"
          className={css`
            font-weight: 400 !important;
          `}>
          Privacy Notice
        </Heading>
        <Box spacingInset="400 0">
          {Object.keys(privacyNoticeTitle).map(type => (
            <Row>
              <Button
                className={css`
                  & {
                    padding: 4px;
                  }
                `}
                type="link"
                onClick={() => onPrivacyNoticeNavButtonLink(type)}>
                <b>{privacyNoticeTitle[type]}</b>
              </Button>
            </Row>
          ))}
        </Box>
        <Box spacingInset="400 0" ref={euPrivacyNoticeRef}>
          <u>
            <b>{privacyNoticeTitle[COUNTRY_MAP.EU]}</b>
          </u>
          <p>September 19, 2023</p>
          <div>
            <p>
              <strong>French</strong>
            </p>
            Les Politique de Confidentialité d'Amazon Carrier Logistics sont
            disponibles ci-dessous en anglais. Vous trouverez également des
            liens vers les traductions{' '}
            <SpanButton
              onClick={() => onTranslationPopupOpen(LOCALES_MAP.FR_FR)}>
              en français
            </SpanButton>
            ,{' '}
            <SpanButton
              onClick={() => onTranslationPopupOpen(LOCALES_MAP.ES_ES)}>
              en espagnol
            </SpanButton>{' '}
            et{' '}
            <SpanButton
              onClick={() => onTranslationPopupOpen(LOCALES_MAP.IT_IT)}>
              en italien
            </SpanButton>{' '}
            de ces documents. En cas de conflit ou de divergence entre une
            version traduite de ces Politique de Confidentialité et la version
            anglaise, la version anglaise prévaut.
          </div>
          <div>
            <p>
              <strong>Italian</strong>
            </p>
            Le le informazioni sulla privacy (Informativa sulla Privacy) di
            Amazon Carrier Logistics sono riportate di seguito in inglese.
            Vengono inoltre forniti i link alle traduzioni in lingua{' '}
            <SpanButton
              onClick={() => onTranslationPopupOpen(LOCALES_MAP.FR_FR)}>
              francese
            </SpanButton>
            ,{' '}
            <SpanButton
              onClick={() => onTranslationPopupOpen(LOCALES_MAP.ES_ES)}>
              spagnola
            </SpanButton>{' '}
            e{' '}
            <SpanButton
              onClick={() => onTranslationPopupOpen(LOCALES_MAP.IT_IT)}>
              italiana
            </SpanButton>
            . In caso di conflitto o discrepanza tra una versione tradotta
            dell’Informativa sulla Privacy e la versione in lingua inglese,
            prevale la versione in lingua inglese.
          </div>
          <div>
            <p>
              <strong>Spanish</strong>
            </p>
            El Aviso de Privacidad de Amazon Carrier Logistics se presentan a
            continuación en Inglés. Nosotros también proporcionamos enlaces a
            las traducciones en{' '}
            <SpanButton
              onClick={() => onTranslationPopupOpen(LOCALES_MAP.FR_FR)}>
              Francés
            </SpanButton>
            ,{' '}
            <SpanButton
              onClick={() => onTranslationPopupOpen(LOCALES_MAP.ES_ES)}>
              Español
            </SpanButton>{' '}
            e{' '}
            <SpanButton
              onClick={() => onTranslationPopupOpen(LOCALES_MAP.IT_IT)}>
              Italiano
            </SpanButton>
            . En caso de discrepancia entre una versión traducida de este Aviso
            de Privacidad y la versión en Inglés, esta última prevalecerá.
          </div>
          <p>
            <strong>English</strong>
          </p>
          <div
            dangerouslySetInnerHTML={{
              __html: marked.parse(euPrivacyNotice[LOCALES_MAP.EN_US]),
            }}></div>
        </Box>
        <Divider />
        <Box spacingInset="400 0" ref={usPrivacyNoticeRef}>
          <u>
            <b>{privacyNoticeTitle[COUNTRY_MAP.US]}</b>
          </u>
          <p>September 19, 2023</p>

          <div
            dangerouslySetInnerHTML={{
              __html: marked.parse(usPrivacyNotice[LOCALES_MAP.EN_US]),
            }}></div>
        </Box>
        <Divider />
        <Box spacingInset="400 0" ref={auPrivacyNoticeRef}>
          <u>
            <b>{privacyNoticeTitle[COUNTRY_MAP.AU]}</b>
          </u>
          <p>October 18, 2023</p>

          <div
            dangerouslySetInnerHTML={{
              __html: marked.parse(auPrivacyNotice[LOCALES_MAP.EN_US]),
            }}></div>
        </Box>
        <Divider />
        <Box spacingInset="400 0" ref={caPrivacyNoticeRef}>
          <u>
            <b>{privacyNoticeTitle[COUNTRY_MAP.CA]}</b>
          </u>
          <p>December 28, 2023</p>

          <div
            dangerouslySetInnerHTML={{
              __html: marked.parse(caPrivacyNotice[LOCALES_MAP.EN_US]),
            }}></div>
        </Box>
        <Divider />
        <Box spacingInset="400 0" ref={aePrivacyNoticeRef}>
          <u>
            <b>{privacyNoticeTitle[COUNTRY_MAP.AE]}</b>
          </u>
          <p>February 28, 2024</p>

          <div
            dangerouslySetInnerHTML={{
              __html: marked.parse(aePrivacyNotice[LOCALES_MAP.EN_US]),
            }}></div>
        </Box>
        <Divider />
        <Box spacingInset="400 0" ref={brPrivacyNoticeRef}>
          <u>
            <b>{privacyNoticeTitle[COUNTRY_MAP.BR]}</b>
          </u>
          <p>March 14, 2024</p>

          <div
            dangerouslySetInnerHTML={{
              __html: marked.parse(brPrivacyNotice[LOCALES_MAP.EN_US]),
            }}></div>
        </Box>
        <Divider />
        <Box spacingInset="400 0" ref={inPrivacyNoticeRef}>
          <u>
            <b>{privacyNoticeTitle[COUNTRY_MAP.IN]}</b>
          </u>
          <p>February 12, 2024</p>

          <div
            dangerouslySetInnerHTML={{
              __html: marked.parse(inPrivacyNotice[LOCALES_MAP.EN_US]),
            }}></div>
        </Box>
        <Divider />
        <Box spacingInset="400 0" ref={mxPrivacyNoticeRef}>
          <u>
            <b>{privacyNoticeTitle[COUNTRY_MAP.MX]}</b>
          </u>
          <p>March 26, 2024</p>

          <div
            dangerouslySetInnerHTML={{
              __html: marked.parse(mxPrivacyNotice[LOCALES_MAP.EN_US]),
            }}></div>
        </Box>
      </article>
    </>
  );
};

export default PrivacyNotice;
